import { useScienceTutorSection } from './scienceTutorContext';
import { Calculator, CheckCircle2, Lightbulb, ShieldAlert } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  ELECTROLYSIS_CASES,
  FARADAY_CONSTANT,
  SECONDS_PER_MINUTE,
  UNSUPPORTED_ELECTROLYTES,
  calculateFaraday,
  checkFaradayMass,
  evaluateElectrodePrediction,
  findElectrolysisCase,
  halfEquationText,
  type ElectrodeProduct,
  type ElectrolysisCase,
} from './electrochemicalCells';
import { ElectrolysisExplore } from './ElectrochemicalElectrolysisExplore';
import { ElectrolysisSchematic } from './ElectrochemicalSchematics';

const CURRENT_SLIDER = { min: 0.5, max: 10, step: 0.5, initial: 2 };
const TIME_SLIDER = { min: 5, max: 120, step: 5, initial: 30 };

function labelOf(options: ElectrodeProduct[], id: string): string {
  return options.find((option) => option.id === id)?.label ?? '';
}

function ProductChoice({
  legend,
  options,
  value,
  onChange,
}: {
  legend: string;
  options: ElectrodeProduct[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <fieldset>
      <legend className="text-sm font-extrabold text-ink">{legend}</legend>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={value === option.id}
            onClick={() => onChange(option.id)}
            className={`rounded-full border px-4 py-2 text-sm font-extrabold ${value === option.id ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'}`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

type FaradayView = 'explore' | 'practice';

/** Faraday's law: Explore (own inputs, working always visible) or Practice (answer first, working after Check). */
function FaradaySection({ item }: { item: ElectrolysisCase }) {
  const [view, setView] = useState<FaradayView>('explore');
  return (
    <section className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          <Calculator className="h-4 w-4" aria-hidden="true" /> Faraday calculation
        </p>
        <div className="grid grid-cols-2 rounded-full bg-surface p-1" role="group" aria-label="Faraday view">
          {(['explore', 'practice'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={view === option}
              onClick={() => setView(option)}
              className={`rounded-full px-4 py-1.5 text-sm font-extrabold ${view === option ? 'bg-ink text-white' : 'text-slate2'}`}
            >
              {option === 'explore' ? 'Explore' : 'Practice'}
            </button>
          ))}
        </div>
      </div>
      <h2 className="mt-2 text-xl font-extrabold text-ink">
        What mass of {item.faraday.productLabel} forms at the cathode?
      </h2>
      {view === 'explore' ? <ElectrolysisExplore item={item} /> : <FaradayPractice key={item.id} item={item} />}
    </section>
  );
}

function FaradayPractice({ item }: { item: ElectrolysisCase }) {
  const [currentA, setCurrentA] = useState(CURRENT_SLIDER.initial);
  const [timeMin, setTimeMin] = useState(TIME_SLIDER.initial);
  const [answer, setAnswer] = useState('');
  const [checked, setChecked] = useState(false);
  useScienceTutorSection('FaradayPanel', { currentA, timeMin, answer, checked });
  const result = useMemo(() => calculateFaraday(item, currentA, timeMin), [item, currentA, timeMin]);
  const feedback =
    checked && result.status === 'ok' ? checkFaradayMass(item, result, Number(answer)) : null;

  return (
    <div>
      <p className="mt-2 text-sm font-medium leading-6 text-slate2">
        n(e⁻) = I·t / F, with F = {FARADAY_CONSTANT.toLocaleString('en-AU')} C mol⁻¹. Each{' '}
        {item.faraday.productLabel} formula unit needs {item.faraday.electronsPerFormula} electron
        {item.faraday.electronsPerFormula === 1 ? '' : 's'}; M = {item.faraday.molarMassGmol} g mol⁻¹.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block text-sm font-extrabold text-ink">
          <span className="flex items-center justify-between">
            <span>Current</span>
            <strong>{currentA.toFixed(1)} A</strong>
          </span>
          <input
            aria-label="Current"
            type="range"
            min={CURRENT_SLIDER.min}
            max={CURRENT_SLIDER.max}
            step={CURRENT_SLIDER.step}
            value={currentA}
            onChange={(event) => {
              setCurrentA(Number(event.target.value));
              setChecked(false);
            }}
            className="mt-3 w-full accent-brand-coral"
          />
        </label>
        <label className="block text-sm font-extrabold text-ink">
          <span className="flex items-center justify-between">
            <span>Time</span>
            <strong>{timeMin} min</strong>
          </span>
          <input
            aria-label="Time"
            type="range"
            min={TIME_SLIDER.min}
            max={TIME_SLIDER.max}
            step={TIME_SLIDER.step}
            value={timeMin}
            onChange={(event) => {
              setTimeMin(Number(event.target.value));
              setChecked(false);
            }}
            className="mt-3 w-full accent-brand-sky"
          />
        </label>
      </div>
      <label className="mt-4 block max-w-xs text-sm font-extrabold text-ink">
        Your answer (g)
        <input
          type="text"
          inputMode="decimal"
          value={answer}
          onChange={(event) => {
            setAnswer(event.target.value);
            setChecked(false);
          }}
          className="mt-2 w-full rounded-2xl border border-hairline bg-white px-4 py-3 font-bold text-ink outline-none focus:border-brand-sky"
        />
      </label>
      <button
        type="button"
        disabled={answer.trim() === ''}
        onClick={() => setChecked(true)}
        className="btn-pill-primary mt-4 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Check mass
      </button>
      {result.status === 'invalid' ? (
        <p role="status" className="mt-4 text-sm font-bold text-ink">
          {result.reason}
        </p>
      ) : null}
      {feedback && result.status === 'ok' ? (
        <div
          role="status"
          aria-label="Faraday feedback"
          className={`mt-4 rounded-2xl border p-4 ${feedback.status === 'correct' ? 'border-brand-mint/35 bg-wash-mint' : 'border-brand-coral/30 bg-wash-coral'}`}
        >
          <p className="text-sm font-extrabold text-ink">{feedback.message}</p>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm font-medium text-slate2">
            <li>
              Q = I·t = {currentA.toFixed(1)} A × {timeMin * SECONDS_PER_MINUTE} s ={' '}
              {result.chargeC.toLocaleString('en-AU')} C
            </li>
            <li>
              n(e⁻) = {result.chargeC.toLocaleString('en-AU')} ÷ {FARADAY_CONSTANT.toLocaleString('en-AU')} ={' '}
              {result.electronMol.toPrecision(3)} mol
            </li>
            <li>
              n({item.faraday.productLabel}) = {result.electronMol.toPrecision(3)} ÷{' '}
              {item.faraday.electronsPerFormula} = {result.productMol.toPrecision(3)} mol
            </li>
            <li>
              m = {result.productMol.toPrecision(3)} × {item.faraday.molarMassGmol} ={' '}
              {result.massG.toPrecision(3)} g
            </li>
          </ol>
        </div>
      ) : null}
      <p className="mt-3 text-xs font-bold leading-5 text-slate2">
        Assumes all of the charge forms this product (100% current efficiency).
      </p>
    </div>
  );
}

export function ElectrolysisMode() {
  const [caseId, setCaseId] = useState(ELECTROLYSIS_CASES[0].id);
  const [cathodeChoice, setCathodeChoice] = useState('');
  const [anodeChoice, setAnodeChoice] = useState('');
  const [revealed, setRevealed] = useState(false);
  useScienceTutorSection('ElectrolysisMode', { caseId, cathodeChoice, anodeChoice, revealed });
  const lookup = findElectrolysisCase(caseId);

  const choose = (id: string) => {
    setCaseId(id);
    setCathodeChoice('');
    setAnodeChoice('');
    setRevealed(false);
  };

  const item = lookup.status === 'ok' ? lookup.item : null;
  const feedback =
    item && revealed ? evaluateElectrodePrediction(item, cathodeChoice, anodeChoice) : null;
  const groups: { title: string; cases: ElectrolysisCase[] }[] = [
    { title: 'Molten', cases: ELECTROLYSIS_CASES.filter((entry) => entry.kind === 'molten') },
    { title: 'Aqueous (reviewed list)', cases: ELECTROLYSIS_CASES.filter((entry) => entry.kind === 'aqueous') },
  ];

  return (
    <section className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
      <aside className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        {groups.map((group) => (
          <div key={group.title} className="mb-5">
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
              {group.title}
            </p>
            <div className="mt-3 grid gap-2">
              {group.cases.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  aria-pressed={entry.id === caseId}
                  onClick={() => choose(entry.id)}
                  className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold text-ink ${entry.id === caseId ? 'border-brand-sky bg-wash-sky' : 'border-hairline bg-surface hover:bg-wash-sky'}`}
                >
                  {entry.name}
                </button>
              ))}
            </div>
          </div>
        ))}
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          Not predicted by this model
        </p>
        <div className="mt-3 grid gap-2">
          {UNSUPPORTED_ELECTROLYTES.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-pressed={entry.id === caseId}
              onClick={() => choose(entry.id)}
              className={`rounded-2xl border border-dashed px-4 py-3 text-left text-sm font-extrabold text-slate2 ${entry.id === caseId ? 'border-brand-coral bg-wash-coral' : 'border-hairline bg-surface'}`}
            >
              {entry.name}
            </button>
          ))}
        </div>
        <p className="mt-4 text-xs font-bold leading-5 text-slate2">
          Aqueous products are predicted only for the reviewed cases above. Anything else fails
          closed instead of guessing.
        </p>
      </aside>

      <div className="min-w-0 space-y-6">
        <section className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
          {item ? (
            <>
              <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
                {item.kind === 'molten' ? 'Molten electrolyte' : 'Aqueous electrolyte'} ·{' '}
                {item.electrodes}
              </p>
              <h2 className="mt-2 text-2xl font-extrabold text-ink">{item.name}</h2>
              <div className="mt-5 rounded-[1.75rem] border border-brand-sky/20 bg-surface p-3">
                <ElectrolysisSchematic
                  item={item}
                  revealed={revealed}
                  cathodeProduct={labelOf(item.cathodeOptions, item.cathode.productId)}
                  anodeProduct={labelOf(item.anodeOptions, item.anode.productId)}
                />
              </div>
              <div className="mt-6 grid gap-4">
                <ProductChoice
                  legend="Predict the product at the cathode (−)"
                  options={item.cathodeOptions}
                  value={cathodeChoice}
                  onChange={(value) => {
                    setCathodeChoice(value);
                    setRevealed(false);
                  }}
                />
                <ProductChoice
                  legend="Predict the product at the anode (+)"
                  options={item.anodeOptions}
                  value={anodeChoice}
                  onChange={(value) => {
                    setAnodeChoice(value);
                    setRevealed(false);
                  }}
                />
              </div>
              <button
                type="button"
                disabled={!cathodeChoice || !anodeChoice}
                onClick={() => setRevealed(true)}
                className="btn-pill-primary mt-5 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Switch on the power
              </button>

              {feedback ? (
                <div role="status" className="mt-6 grid gap-3 sm:grid-cols-2">
                  {[
                    {
                      key: 'cathode',
                      title: 'Cathode (−): reduction',
                      correct: feedback.cathodeCorrect,
                      message: feedback.cathodeMessage,
                      half: item.cathode.half,
                    },
                    {
                      key: 'anode',
                      title: 'Anode (+): oxidation',
                      correct: feedback.anodeCorrect,
                      message: feedback.anodeMessage,
                      half: item.anode.half,
                    },
                  ].map((entry) => (
                    <div
                      key={entry.key}
                      className={`rounded-2xl border p-4 ${entry.correct ? 'border-brand-mint/35 bg-wash-mint' : 'border-brand-coral/30 bg-wash-coral'}`}
                    >
                      <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wide text-ink-soft">
                        {entry.correct ? (
                          <CheckCircle2 className="h-4 w-4 text-brand-mint" aria-hidden="true" />
                        ) : (
                          <Lightbulb className="h-4 w-4 text-brand-coral" aria-hidden="true" />
                        )}
                        {entry.title} · {entry.correct ? 'matches' : 'check this'}
                      </p>
                      <p className="mt-2 text-sm font-medium leading-6 text-slate2">{entry.message}</p>
                      <p className="mt-2 break-words text-sm font-extrabold text-ink">
                        {halfEquationText(entry.half)}
                      </p>
                    </div>
                  ))}
                  {item.note ? (
                    <p className="rounded-2xl bg-wash-sunshine p-4 text-sm font-bold leading-6 text-ink sm:col-span-2">
                      {item.note}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : (
            <div role="status" className="flex items-start gap-3 rounded-2xl border border-brand-coral/30 bg-wash-coral p-5">
              <ShieldAlert className="mt-0.5 h-6 w-6 shrink-0 text-brand-coral" aria-hidden="true" />
              <div>
                <h2 className="font-extrabold text-ink">Not in the reviewed case list</h2>
                <p className="mt-1 text-sm font-medium leading-6 text-slate2">
                  {lookup.status === 'unsupported' ? lookup.reason : null} Ask your teacher or check
                  a reviewed reference before predicting these products.
                </p>
              </div>
            </div>
          )}
        </section>

        {item ? <FaradaySection item={item} /> : null}

        <section className="rounded-3xl border border-hairline bg-surface p-5 text-sm font-medium leading-6 text-slate2">
          <strong className="text-ink">Safety and model limit:</strong> molten-salt electrolysis
          and chlorine production are hazardous and are demonstrated only under teacher supervision
          with a risk assessment. This model predicts products for reviewed cases; real cells also
          depend on concentration, electrode material, current density and overpotential.
        </section>
      </div>
    </section>
  );
}
